import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ArrowRight } from 'lucide-react';
import { useAllTopics } from '@/hooks/useAllTopics';
import { useIsMobile } from '@/hooks/use-mobile';
import SEO from '@/components/SEO';
import { supabase } from '@/integrations/supabase/client';

interface AnimatedTopicItemProps {
  children: React.ReactNode;
  delay?: number;
}

const AnimatedTopicItem: React.FC<AnimatedTopicItemProps> = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </div>
  );
};

const VideoLibrary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { topics } = useAllTopics();
  const [dbCounts, setDbCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    // Count unique videos per category across both the primary category_id
    // on `videos` and additional assignments in `video_categories`
    (async () => {
      const [primaryRes, junctionRes] = await Promise.all([
        supabase.from('videos').select('id, category_id'),
        supabase.from('video_categories').select('video_id, category_id'),
      ]);
      const perCat = new Map<string, Set<string>>();
      const add = (cat: string, vid: string) => {
        if (!perCat.has(cat)) perCat.set(cat, new Set());
        perCat.get(cat)!.add(vid);
      };
      (primaryRes.data || []).forEach((v: any) => add(v.category_id, v.id));
      (junctionRes.data || []).forEach((r: any) => add(r.category_id, r.video_id));
      const counts: Record<string, number> = {};
      perCat.forEach((set, cat) => { counts[cat] = set.size; });
      setDbCounts(counts);
    })();
  }, []);

  const filteredTopics = topics.filter(topic =>
    topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    topic.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedTopics = filteredTopics;

  return (
    <div className="min-h-screen relative">
      <Header />
      <SEO
        title="Video & Documents Library | Freeman & Crown Law"
        description="Explore our comprehensive collection of educational videos covering constitutional law, legal processes, and rights protection. Learn from expert constitutional attorneys."
        image="/lovable-uploads/8c630ffa-2bac-4996-be73-277885cc03f3.png"
        url={window.location.origin + '/law-library'}
        type="website"
      />
      
      {/* Loading state */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
      
      {/* Auth overlay when user is not logged in */}
      {!user && !loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-lg border-2 border-primary/30 shadow-2xl bg-background/95">
            <CardContent className="pt-6 pb-6 sm:pt-8 sm:pb-8 px-6 text-center space-y-5">
              <div className="space-y-2.5">
                <h2 className="text-2xl sm:text-3xl font-rockwell font-bold text-primary">
                  Private Members Only
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground">
                  You need to be logged in to access our exclusive library of educational videos and resources
                </p>
                <p className="text-sm text-muted-foreground/80">
                  Don't have an account? Sign up for free!
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button 
                  onClick={() => navigate('/auth')}
                  className="w-full sm:flex-1"
                  size="default"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate('/auth?tab=signup')}
                  variant="outline"
                  className="w-full sm:flex-1"
                  size="default"
                >
                  Join Free
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <main className="pt-32 pb-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Video & Documents Library
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Let your curiosity guide you as you explore this comprehensive collection of educational videos covering various legal topics and processes. Be smart as you tred.
            </p>
            
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            {displayedTopics.map((topic, index) => (
              <div
                key={topic.id}
                className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 cursor-pointer mb-6"
                onClick={() => navigate(`/category/${topic.id}`)}
              >
                {isMobile ? (
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-4">
                      <h3 className="text-xl font-semibold text-foreground">
                        {topic.title}
                      </h3>
                      <p className="mt-2 text-muted-foreground">
                        {topic.description}
                      </p>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex gap-4">
                          <span className="text-sm text-muted-foreground font-bold">
                            {(topic.videoCount || 0) + (dbCounts[topic.id] || 0)} videos
                          </span>
                          <span className="text-sm text-muted-foreground font-bold">
                            0 resources
                          </span>
                        </div>
                        <div className="flex items-center text-sm font-medium text-primary">
                          <span>Explore</span>
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-foreground">
                          {topic.title}
                        </h3>
                        <p className="mt-2 text-muted-foreground">
                          {topic.description}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-muted-foreground block">
                          {(topic.videoCount || 0) + (dbCounts[topic.id] || 0)} videos
                        </span>
                        <span className="text-sm text-muted-foreground block">
                          0 resources
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-start text-sm font-medium text-primary mt-4">
                      <span>Explore</span>
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VideoLibrary;